import { ConductorSingleChild, Router, ScreenBase, watchBusy } from "@frui.ts/screens";
import BusyWatcher from "@frui.ts/screens/dist/structure/busyWatcher";
import { validate } from "@frui.ts/validation";
import { action, observable } from "mobx";
import LocalizationService from "services/localizationService";
import Vehicle from "models/vehicle";
import ChargeRegistrationService from "services/chargeRegistrationService";
import AppRouter from "services/appRouter";
import VehicleInfoViewModel from "../common/vehicleInfoViewModel";
import ValidationSummaryViewModel, { ValidationSummaryNavigationName } from "./validationSummaryViewModel";
import { czechID } from "../../config";
import { scrollToElement } from "utils/scrollTo";
import ChargeTypesService from "services/chargeTypesService";
import FuelTypeResponse from "entities/fuelTypeResponse";
import EnumService from "services/enumService";

export const PaymentValidationModuleNavigationName = "validation";

@Router.registerRoute({
  route: PaymentValidationModuleNavigationName,
  name: Router.Self,
  children: [ValidationSummaryViewModel],
})
export default class PaymentValidationViewModel extends ConductorSingleChild<ScreenBase> {
  public vehicleInfoViewModel: VehicleInfoViewModel;
  private validationSummaryViewModel?: ValidationSummaryViewModel;

  busyWatcher = new BusyWatcher();

  @observable vehicle: Vehicle;
  @observable errorMessage?: string;
  @observable childElement?: HTMLElement;

  constructor(
    public localizationService: LocalizationService,
    private validationSummaryViewModelFactory: ReturnType<typeof ValidationSummaryViewModel.Factory>,
    private vehicleInfoViewModelFactory: ReturnType<typeof VehicleInfoViewModel.Factory>,
    private chargeRegistrationService: ChargeRegistrationService,
    private chargeTypesService: ChargeTypesService,
    private enumService: EnumService,
    private router: AppRouter
  ) {
    super();
    this.nameValue = "validation.title";
    this.navigationName = PaymentValidationModuleNavigationName;
    this.vehicle = new Vehicle();
    this.vehicle.countryId = czechID;
    this.vehicleInfoViewModel = this.vehicleInfoViewModelFactory(this.vehicle, { attachValidator: true, breakpoint: "xxl" });
  }

  protected async onInitialize(): Promise<any> {
    await this.chargeTypesService.startInitialization(true);
    this.enumService.values("fuel_types") as FuelTypeResponse[];
    return super.onInitialize();
  }

  closeChild(child: ValidationSummaryViewModel, forceClose = false) {
    this.vehicle = new Vehicle();
    if (child.vehicle.countryId) {
      this.vehicle.countryId = child.vehicle.countryId;
    }
    this.vehicleInfoViewModel = this.vehicleInfoViewModelFactory(this.vehicle, { attachValidator: true, breakpoint: "xxl" });
    this.chargeRegistrationService.reset();

    return super.closeChild(child, forceClose);
  }

  @watchBusy
  async navigate(subPath: string | undefined, params: any): Promise<void> {
    if (subPath === ValidationSummaryNavigationName && params?.countryId && params?.licensePlate) {
      const countryId = (this.vehicle.countryId = params.countryId);
      const licensePlate = (this.vehicle.licensePlate = params.licensePlate);

      try {
        this.errorMessage = undefined;
        await this.chargeRegistrationService.getChargeRegistration(countryId, licensePlate);
        this.validationSummaryViewModel = this.validationSummaryViewModelFactory(
          this.vehicle,
          this.chargeRegistrationService.chargeRegistrationsResponse
        );
        if (window.innerWidth <= 991) {
          scrollToElement(this.childElement);
        }
      } catch (e) {
        this.validationSummaryViewModel = undefined;
        this.errorMessage = "errorAtCommunicationWithBackend";
      }
    }
    return super.navigate(subPath, params);
  }

  protected findNavigationChild(navigationName: string | undefined): Promise<ScreenBase | undefined> | ScreenBase | undefined {
    if (navigationName === ValidationSummaryNavigationName && this.validationSummaryViewModel) {
      return this.validationSummaryViewModel;
    }

    return undefined;
  }

  @action.bound
  @watchBusy
  async validate() {
    if (!validate(this.vehicle)) {
      return;
    }

    const countryId = this.vehicle.countryId;
    const licensePlate = this.vehicle.licensePlate;

    this.router.navigate(
      ValidationSummaryViewModel,
      {},
      {
        countryId,
        licensePlate,
      }
    );
  }
}
